// src/components/more/MeasurementTab.js
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

const monthArray = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const MeasurementTab = ({ measurements }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    if (!measurements || measurements.length === 0) {
        return (
            <div className="p-4 text-center text-gray-400">
                No measurement data available.
            </div>
        );
    }

    const sortedArray =
        measurements[currentIndex]?.data?.sort((a, b) => a.month - b.month) || [];

    const start = sortedArray[0];
    const latest = sortedArray[sortedArray.length - 1];
    const selectedYear = measurements[currentIndex]?.year || '—';

    const heightChange =
        latest && start ? (latest.height - start.height).toFixed(2) : '0.00';
    const weightChange =
        latest && start ? (latest.weight - start.weight).toFixed(2) : '0.00';

    const handleYearChange = (direction) => {
        if (direction === 'prev') {
            setCurrentIndex((prev) =>
                prev < measurements.length - 1 ? prev + 1 : 0
            );
        } else {
            setCurrentIndex((prev) =>
                prev > 0 ? prev - 1 : measurements.length - 1
            );
        }
    };

    return (
        <>
            {/* Summary Card */}
            {sortedArray.length > 0 && (
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                    <div className="grid grid-cols-3 gap-3 text-center">
                        {/* Start */}
                        <div className="border-r border-gray-200">
                            <p className="text-lg md:text-2xl font-bold text-gray-800">
                                {start?.height?.toFixed(2)} cm
                            </p>
                            <p className="text-normal md:text-xl font-bold text-gray-800 mt-1">
                                {start?.weight?.toFixed(2)} kg
                            </p>
                            <p className="text-xs text-gray-500 mt-2">Start</p>
                        </div>

                        {/* Middle: Today / End */}
                        <div className="border-r border-gray-200">
                            <p className="text-lg md:text-2xl font-bold text-cyan-600">
                                {latest?.height?.toFixed(2)} cm
                            </p>
                            <p className="text-normal md:text-xl font-bold text-cyan-600 mt-1">
                                {latest?.weight?.toFixed(2)} kg
                            </p>
                            <p className="text-xs text-gray-500 mt-2">
                                {selectedYear !== '—' && Number(selectedYear) === new Date().getFullYear()
                                    ? 'Current'
                                    : 'End'}
                            </p>
                        </div>

                        {/* Change */}
                        <div>
                            <p
                                className={`text-lg md:text-2xl font-bold ${heightChange >= 0 ? 'text-green-600' : 'text-red-600'
                                    }`}
                            >
                                {heightChange > 0 ? `+${heightChange}` : heightChange} cm
                            </p>
                            <p
                                className={`text-normal md:text-xl font-bold ${weightChange >= 0 ? 'text-green-600' : 'text-red-600'
                                    } mt-1`}
                            >
                                {weightChange > 0 ? `+${weightChange}` : weightChange} kg
                            </p>
                            <p className="text-xs text-gray-500 mt-2">Change</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Year Selector */}
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl p-4">
                <div className="flex items-center justify-between">
                    <button
                        onClick={() => handleYearChange('prev')}
                        className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center"
                    >
                        <ChevronLeft className="w-5 h-5 text-white" />
                    </button>
                    <div className="flex items-center space-x-2 text-white">
                        <Calendar className="w-5 h-5" />
                        <span className="text-lg font-bold">{selectedYear}</span>
                    </div>
                    <button
                        onClick={() => handleYearChange('next')}
                        className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center"
                    >
                        <ChevronRight className="w-5 h-5 text-white" />
                    </button>
                </div>
            </div>

            {/* Monthly Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="grid grid-cols-3 bg-gray-50 p-4 font-bold text-gray-700 text-sm border-b">
                    <div>MONTH</div>
                    <div className="text-center">HEIGHT</div>
                    <div className="text-center">WEIGHT</div>
                </div>

                {sortedArray.length > 0 ? (
                    sortedArray.map((data, idx) => (
                        <div
                            key={idx}
                            className="grid grid-cols-3 p-4 border-b border-gray-100 last:border-0"
                        >
                            <div className="text-gray-800 font-medium">
                                {monthArray[data.month] || '—'}
                            </div>
                            <div className="text-center text-gray-600">
                                {data.height?.toFixed(2)} cm
                            </div>
                            <div className="text-center text-gray-600">
                                {data.weight?.toFixed(2)} kg
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="p-4 text-center text-gray-400">
                        No data available.
                    </div>
                )}
            </div>
        </>
    );
};

export default MeasurementTab;
